import React, { useState } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { FormControl } from "react-bootstrap"

import { CellWrapper } from "app/static/frontend/sheets/components/cells/MantineCells/CellWrapper"

import { editResourceTableRow, patchColumnData, unclickCell } from "app/static/frontend/sheets/sheet-action-creators"
import { ENTERKEY, ESCAPEKEY } from "shared/imports/sharedConstants"

/**
 * Component to render an input cell in sheets
 * Use a stateful component so that we don't update the store as the user is typing a value
 * This prevents a wierd case where if you have sorted a column and are editing the cell,
 * the cell jumps down/up because the sorting happens nearly instantly as the data is edited
 */
const InputCellComponent = ({
    type,
    unclickCell,
    sheetId,
    isResourceTable,
    editResourceTableRow,
    patchColumnData,
    sendRowIds,
    menuShouldScrollIntoView,
    placeholder,
    children,
    rowId,
    field,
    columnId,
    row,
    ...props
}) => {
    const [currentVal, setCurrentVal] = useState(children)

    const handleKeyUp = (e) => {
        // submit if the user presses the enter key
        if (e.which === ENTERKEY && type !== "textarea") {
            e.target.blur()
            // don't submit if the user hits escape
        } else if (e.which === ESCAPEKEY) {
            unclickCell(sheetId)
        }
    }

    const handleBlur = (e) => {
        let { value } = e.target
        if (!(value && value.length)) {
            // Needs to pass more than an empty string, otherwise null/undefined
            // get rendered as 0
            value = null
        }
        if (
            value === children ||
            (value === null && children === undefined) ||
            (value === undefined && children === null)
        ) {
            unclickCell(sheetId)
        } else {
            if (isResourceTable) {
                editResourceTableRow(sheetId, rowId, { [field.name]: value })
            } else {
                patchColumnData({
                    id: sheetId,
                    columnId,
                    rowId,
                    newValue: value,
                    includeRowIds: sendRowIds,
                    row: row,
                })
            }
        }
    }

    return (
        <CellWrapper {...props} style={{ ...props.style, padding: "0.5rem" }}>
            <FormControl
                type={type || "text"}
                {...(type === "textarea" ? { as: type } : {})}
                value={currentVal}
                // prevent sheet from scrolling on arrow keys when editing
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={handleKeyUp}
                onChange={(e) => setCurrentVal(e.target.value)}
                onBlur={handleBlur}
                menuShouldScrollIntoView={menuShouldScrollIntoView}
                placeholder={placeholder}
                style={{ padding: ".25rem .5rem" }}
            />
        </CellWrapper>
    )
}

InputCellComponent.propTypes = {
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    columnId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    field: PropTypes.object.isRequired,
    isResourceTable: PropTypes.bool,
    menuShouldScrollIntoView: PropTypes.bool,
    patchColumnData: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sheetId: PropTypes.string.isRequired,
    unclickCell: PropTypes.func.isRequired,
}

InputCellComponent.defaultProps = {
    children: "",
    isResourceTable: false,
    menuShouldScrollIntoView: false,
    placeholder: "",
    sendRowIds: false,
}

export const InputCell = connect(null, { patchColumnData, editResourceTableRow, unclickCell })(InputCellComponent)
